/**
 * PATH: packages/email-hub/src/feature/reader-header/command/CommandBar.tsx
 * FILE_ID: EMAILHUB-READERHEADER-COMMAND-BAR-001
 * PURPOSE: Render one stable 44px navigation, subject, and action band.
 * ROLE IN ARCHITECTURE: Presentational leaf of the shared reader-header frame.
 * MAIN EXPORTS: ReaderCommandBar, ReaderCommandAction.
 * NON-RESPONSIBILITIES: Callers own mutations, navigation, and permissions.
 * NOTES FOR FUTURE AI: Core action order is fixed; add long-tail actions to overflow.
 */
import { useRef, useState, type ReactNode } from 'react'
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Forward,
  Loader2,
  Maximize2,
  MoreHorizontal,
  Reply,
  ReplyAll,
  Search,
  X,
} from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { ReaderActionId } from '../model/types'
import type { ReaderHeaderDensity } from '../responsive/density'
import { COMMAND_BAR_H, MOTION_MS } from '../tokens'
import { ReaderMoreMenu } from './MoreMenu'
import { useCommandBarScrollShadow } from './useCommandBarScrollShadow'

/** A caret submenu row on an overflow action (registry contextActions,
 *  see menus/overflowRowItems.ts). Never rendered for core/command-bar
 *  actions — the caret affordance is a More-menu-only contract. */
export interface ReaderCommandSubAction {
  id: string
  label: string
  onClick: () => void
  dangerous?: boolean
}

export interface ReaderCommandAction {
  id: ReaderActionId
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  busy?: boolean
  dangerous?: boolean
  /** Present only when the action declares registry contextActions AND the
   *  host resolved at least one to a concrete row (menus/overflowRowItems.ts)
   *  — MoreMenu renders a caret/submenu for any row with a non-empty list. */
  subActions?: readonly ReaderCommandSubAction[]
}

export interface ReaderCommandBarProps {
  density: ReaderHeaderDensity
  subject: string
  narrow: boolean
  positionLabel?: string
  onBack: () => void
  backLabel: string
  previous?: ReaderCommandAction
  next?: ReaderCommandAction
  reply: ReaderCommandAction
  replyAll?: ReaderCommandAction
  forward?: ReaderCommandAction
  done?: ReaderCommandAction
  overflowActions: readonly ReaderCommandAction[]
  onFind: () => void
  onExpand?: () => void
}

function CoreActionButton({
  action,
  kind,
}: {
  action: ReaderCommandAction
  kind: 'reply' | 'reply_all' | 'forward' | 'done'
}) {
  const Icon = {
    reply: Reply,
    reply_all: ReplyAll,
    forward: Forward,
    done: CheckCircle2,
  }[kind]
  const primary = kind === 'reply'
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled || action.busy}
      // WORK ITEM 10: wire the press feedback to the MOTION_MS.press token
      // (80ms) instead of the unmatched `duration-100` static class — this
      // button is the one true hover/press affordance for Reply/Forward/Done.
      style={{ transitionDuration: `${MOTION_MS.press}ms` }}
      className={cn(
        'inline-flex h-7 shrink-0 items-center justify-center gap-1 rounded-full text-[11px] font-medium',
        'transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
        'active:scale-[.97] motion-reduce:transform-none',
        primary
          // Reply is the only LABELLED core action, so on a coarse pointer the
          // shared 44px target made it exactly the bar's height: a full-bleed
          // blue slab with no gap above or below, reading as a desktop button
          // rather than the chip the icon-only actions read as. 32px keeps the
          // chip shape (and a usable target with the label's width behind it)
          // — operator call 2026-08-12, taken over the 44px floor knowingly.
          ? 'bg-primary px-2.5 text-primary-foreground hover:bg-primary/90 pointer-coarse:h-8'
          : 'w-7 pointer-coarse:h-11 pointer-coarse:w-11 text-muted-foreground hover:bg-accent hover:text-foreground',
        (action.disabled || action.busy) && 'cursor-not-allowed opacity-55',
      )}
      title={action.label}
      aria-label={action.label}
      data-reader-command={action.id}
    >
      {action.busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <Icon className="h-3.5 w-3.5" aria-hidden />
      )}
      {primary && <span>Reply</span>}
    </button>
  )
}

export function ReaderCommandBar({
  density,
  subject,
  narrow,
  positionLabel,
  onBack,
  backLabel,
  previous,
  next,
  reply,
  replyAll,
  forward,
  done,
  overflowActions,
  onFind,
  onExpand,
}: ReaderCommandBarProps) {
  const [moreOpen, setMoreOpen] = useState(false)
  const moreTriggerRef = useRef<HTMLButtonElement>(null)
  const barRef = useRef<HTMLDivElement | null>(null)
  const scrolled = useCommandBarScrollShadow(barRef)
  const compact = density === 'compact'
  /**
   * Find is keyboard-first (Ctrl/Cmd+F still works everywhere), so at compact
   * it joins the existing More menu rather than holding a slot in the bar.
   *
   * This is a fit problem, not a taste one. Every control here declares a
   * `pointer-coarse` 44px size, so on touch the fixed chrome costs ~44 back +
   * ~72 Reply + 44 Done + 44 More + 44 Find + 44 Expand + padding and gaps —
   * past the width of a phone-sized reader before the subject gets a pixel.
   * The trailing controls had no `shrink-0`, so the overflow was paid silently
   * by squeezing More/Find/Expand back under the touch floor they ask for.
   * Dropping one control is what makes the row fit; `shrink-0` below is what
   * keeps the rest honest if it ever does not.
   */
  const findAction: ReaderCommandAction = {
    id: 'find',
    label: 'Find in thread',
    icon: <Search className="h-3.5 w-3.5" aria-hidden />,
    onClick: onFind,
  }
  const menuActions = [
    ...(compact && previous ? [previous] : []),
    ...(compact && next ? [next] : []),
    ...(compact && replyAll ? [replyAll] : []),
    ...(compact && forward ? [forward] : []),
    ...(compact ? [findAction] : []),
    ...overflowActions,
  ]

  return (
    <div
      ref={barRef}
      className={cn(
        'sticky top-0 z-20 flex shrink-0 items-center gap-1 border-b border-border/70 bg-background/95 px-3 backdrop-blur-sm',
        'transition-shadow duration-150 motion-reduce:transition-none',
        scrolled && 'shadow-[0_2px_8px_-2px_rgba(0,0,0,0.15)]',
      )}
      style={{ height: COMMAND_BAR_H }}
      data-testid="reader-command-bar"
      data-scrolled={scrolled || undefined}
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 pointer-coarse:h-11 pointer-coarse:w-11"
        title={backLabel}
        aria-label={backLabel}
        data-reader-command="back"
      >
        {narrow ? (
          <ArrowLeft className="h-4 w-4" />
        ) : (
          <X className="h-4 w-4" />
        )}
      </button>
      {positionLabel && (
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {positionLabel}
        </span>
      )}
      {!compact && previous && next && (
        <div className="flex shrink-0 items-center">
          {[previous, next].map((action, index) => {
            const Icon = index === 0 ? ChevronLeft : ChevronRight
            return (
              <button
                key={action.id}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 pointer-coarse:h-11 pointer-coarse:w-11"
                title={action.label}
                aria-label={action.label}
                data-reader-command={action.id}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            )
          })}
        </div>
      )}
      <h1
        className="min-w-0 flex-1 truncate px-1 text-[13px] font-semibold tracking-[-0.01em] text-foreground"
        title={subject}
      >
        {subject}
      </h1>
      <CoreActionButton action={reply} kind="reply" />
      {!compact && replyAll && (
        <CoreActionButton action={replyAll} kind="reply_all" />
      )}
      {!compact && forward && (
        <CoreActionButton action={forward} kind="forward" />
      )}
      {done && <CoreActionButton action={done} kind="done" />}
      <div className="relative shrink-0">
        <button
          ref={moreTriggerRef}
          type="button"
          onClick={() => setMoreOpen((open) => !open)}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 pointer-coarse:h-11 pointer-coarse:w-11"
          title="More actions"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          data-reader-command="more"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        <ReaderMoreMenu
          actions={menuActions}
          open={moreOpen}
          onOpenChange={setMoreOpen}
          triggerRef={moreTriggerRef}
        />
      </div>
      <span className="mx-0.5 h-4 w-px shrink-0 bg-border/60" aria-hidden />
      {!compact && (
        <button
          type="button"
          onClick={onFind}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 pointer-coarse:h-11 pointer-coarse:w-11"
          title="Find in thread (Ctrl/Cmd+F)"
          aria-label="Find in thread"
          data-reader-command="find"
        >
          <Search className="h-3.5 w-3.5" />
        </button>
      )}
      {onExpand && (
        <button
          type="button"
          onClick={onExpand}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1 pointer-coarse:h-11 pointer-coarse:w-11"
          title="Open full view"
          aria-label="Open full view"
          data-reader-command="expand"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
